import { Button, DatePicker, Form, Input, InputNumber, Rate, Select, Typography } from "antd";
import dayjs from "dayjs";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { loadAllStaff } from "../../redux/rtk/features/user/userSlice";

const { TextArea } = Input;
const { Title } = Typography;
const { Option } = Select;

const AddPerformanceFeedback = ({ form, onSubmit }) => {
	const dispatch = useDispatch();
	const [loader, setLoader] = React.useState(false);
	const staffList = useSelector((state) => state.users?.list || []);

	useEffect(() => {
		dispatch(loadAllStaff({ status: true }));
	}, [dispatch]);

	const onFinish = async (values) => {
		try {
			setLoader(true);
			const feedbackData = {
				...values,
				feedbackDate: dayjs(values.feedbackDate).format("YYYY-MM-DD"),
				overallRating: values.overallRating || 0,
			};

			if (onSubmit) {
				onSubmit(feedbackData);
				toast.success("Performance Feedback Submitted Successfully!");
			}
		} catch (error) {
			toast.error("Failed to submit performance feedback");
		} finally {
			setLoader(false);
		}
	};

	const onFinishFailed = (errorInfo) => {
		toast.warning("Please fill all required fields");
		setLoader(false);
	};

	return (
		<div className="column-design border rounded card-custom">
			<Title level={4} className="m-2 mt-5 mb-5 text-center">
				Performance Feedback
			</Title>
			<Form
				form={form}
				style={{ marginBottom: "40px" }}
				name="performance-feedback-form"
				labelCol={{ span: 8 }}
				wrapperCol={{ span: 16 }}
				onFinish={onFinish}
				onFinishFailed={onFinishFailed}
				autoComplete="off"
				preserve={false}
			>
				<Form.Item
					label={<span className="required-label">Employee</span>}
					name="employeeId"
					rules={[
						{ required: true, message: "Please select an employee!" },
					]}
				>
					<Select
						placeholder="Select employee"
						showSearch
						filterOption={(input, option) =>
							(option?.children ?? "")
								.toLowerCase()
								.includes(input.toLowerCase())
						}
					>
						{staffList?.map((staff) => (
							<Option key={staff.id} value={staff.id}>
								{staff.EmployeeName ||
									`${staff.firstName || ""} ${staff.lastName || ""}`.trim() ||
									staff.UserName}
							</Option>
						))}
					</Select>
				</Form.Item>

				<Form.Item
					label={<span className="required-label">Reviewer</span>}
					name="reviewerId"
					rules={[
						{ required: true, message: "Please select a reviewer!" },
					]}
				>
					<Select
						placeholder="Select reviewer"
						showSearch
						filterOption={(input, option) =>
							(option?.children ?? "")
								.toLowerCase()
								.includes(input.toLowerCase())
						}
					>
						{staffList?.map((staff) => (
							<Option key={staff.id} value={staff.id}>
								{staff.EmployeeName ||
									`${staff.firstName || ""} ${staff.lastName || ""}`.trim() ||
									staff.UserName}
							</Option>
						))}
					</Select>
				</Form.Item>

				<Form.Item
					label={<span className="required-label">Feedback Date</span>}
					name="feedbackDate"
					rules={[
						{ required: true, message: "Please select feedback date!" },
					]}
				>
					<DatePicker
						style={{ width: "100%" }}
						format="DD-MM-YYYY"
						disabledDate={(current) => current && current > dayjs().endOf("day")}
					/>
				</Form.Item>

				<Form.Item
					label={<span className="required-label">Review Period</span>}
					name="reviewPeriod"
					rules={[
						{ required: true, message: "Please select review period!" },
					]}
				>
					<Select placeholder="Select review period">
						<Option value="Monthly">Monthly</Option>
						<Option value="Quarterly">Quarterly</Option>
						<Option value="Semi-Annual">Semi-Annual</Option>
						<Option value="Annual">Annual</Option>
					</Select>
				</Form.Item>

				<Form.Item
					label={<span className="required-label">Overall Rating</span>}
					name="overallRating"
					rules={[
						{ required: true, message: "Please provide overall rating!" },
					]}
				>
					<Rate allowHalf />
				</Form.Item>

				<Form.Item
					label={<span className="required-label">Technical Skills</span>}
					name="technicalSkills"
					rules={[
						{ required: true, message: "Please rate technical skills!" },
					]}
				>
					<Rate allowHalf />
				</Form.Item>

				<Form.Item
					label={<span className="required-label">Communication</span>}
					name="communication"
					rules={[
						{ required: true, message: "Please rate communication!" },
					]}
				>
					<Rate allowHalf />
				</Form.Item>

				<Form.Item
					label={<span className="required-label">Teamwork</span>}
					name="teamwork"
					rules={[
						{ required: true, message: "Please rate teamwork!" },
					]}
				>
					<Rate allowHalf />
				</Form.Item>

				<Form.Item
					label={<span className="required-label">Problem Solving</span>}
					name="problemSolving"
					rules={[
						{ required: true, message: "Please rate problem solving!" },
					]}
				>
					<Rate allowHalf />
				</Form.Item>

				<Form.Item
					label={<span className="required-label">Strengths</span>}
					name="strengths"
					rules={[
						{ required: true, message: "Please enter strengths!" },
					]}
				>
					<TextArea
						rows={3}
						placeholder="List key strengths and achievements"
						maxLength={500}
						showCount
					/>
				</Form.Item>

				<Form.Item
					label={<span className="required-label">Areas for Improvement</span>}
					name="areasForImprovement"
					rules={[
						{ required: true, message: "Please enter areas for improvement!" },
					]}
				>
					<TextArea
						rows={3}
						placeholder="List areas that need improvement"
						maxLength={500}
						showCount
					/>
				</Form.Item>

				<Form.Item
					label="Goals for Next Period"
					name="goals"
				>
					<TextArea
						rows={2}
						placeholder="Set goals for the next review period"
						maxLength={300}
						showCount
					/>
				</Form.Item>

				<Form.Item
					label="Additional Comments"
					name="comments"
				>
					<TextArea
						rows={2}
						placeholder="Any additional comments or notes"
						maxLength={300}
						showCount
					/>
				</Form.Item>

				<Form.Item wrapperCol={{ offset: 8, span: 16 }}>
					<Button
						type="primary"
						size="large"
						htmlType="submit"
						block
						loading={loader}
					>
						Submit Feedback
					</Button>
				</Form.Item>
			</Form>
		</div>
	);
};

export default AddPerformanceFeedback;

